import type { StateMachine } from './machine';

/**
 * Statut d'INTERVENTION (cahier des charges §7-§9). Une intervention appartient à un dossier
 * (pose, entretien, SAV…). Table livrée avec le module Planning.
 * Toute intervention attribuée passe par « À accepter » ; un refus exige un motif et renvoie
 * l'intervention « À réattribuer ».
 */
export const INTERVENTION_STATUSES = [
  'to_assign',
  'proposed',
  'accepted',
  'scheduled',
  'taken_charge',
  'on_site',
  'completed',
  'impossible',
  'cancelled',
] as const;
export type InterventionStatus = (typeof INTERVENTION_STATUSES)[number];

export const interventionMachine: StateMachine<InterventionStatus> = {
  name: 'intervention',
  states: INTERVENTION_STATUSES,
  initial: 'to_assign',
  terminal: ['completed', 'cancelled'],
  labels: {
    to_assign: 'À attribuer',
    proposed: 'À accepter',
    accepted: 'Acceptée',
    scheduled: 'Planifiée',
    taken_charge: 'Prise en charge',
    on_site: 'Sur place / en cours',
    completed: 'Terminée / installée',
    impossible: 'Impossible',
    cancelled: 'Annulée',
  },
  transitions: [
    { from: ['to_assign'], to: 'proposed', permission: 'planning.manage' },
    { from: ['proposed'], to: 'accepted', permission: 'intervention.execute' },
    // l'administrateur valide à la place du technicien (date fixée : aussitôt « Planifiée »)
    { from: ['proposed'], to: 'accepted', permission: 'intervention.validate_for_technician' },
    { from: ['proposed'], to: 'to_assign', permission: 'intervention.execute', reasonCategory: 'intervention_refusal' },
    // le planificateur retire une proposition (réattribution)
    { from: ['proposed'], to: 'to_assign', permission: 'planning.manage', reasonCategory: 'intervention_reassign' },
    { from: ['accepted'], to: 'scheduled', permission: 'planning.manage' },
    { from: ['accepted', 'scheduled'], to: 'to_assign', permission: 'planning.manage', reasonCategory: 'intervention_reassign' },
    { from: ['scheduled'], to: 'taken_charge', permission: 'intervention.execute' },
    { from: ['taken_charge'], to: 'on_site', permission: 'intervention.execute' },
    { from: ['on_site'], to: 'completed', permission: 'intervention.execute' },
    // l'administrateur clôture à la place du technicien (photos reçues directement, chantier partenaire…)
    {
      from: ['proposed', 'accepted', 'scheduled', 'taken_charge', 'on_site'],
      to: 'completed',
      permission: 'intervention.validate_for_technician',
    },
    { from: ['taken_charge', 'on_site'], to: 'impossible', permission: 'intervention.execute', reasonCategory: 'intervention_impossible' },
    { from: ['impossible'], to: 'to_assign', permission: 'planning.manage' },
    {
      from: ['to_assign', 'proposed', 'accepted', 'scheduled', 'impossible'],
      to: 'cancelled',
      permission: 'planning.manage',
      reasonCategory: 'intervention_cancel',
    },
  ],
};
